// src/services/enoceanService.js
// Service for EnOcean button discovery and management
import { axiosInstance } from './apiClient';
import { io } from 'socket.io-client';

// For WebSocket, we need the base URL without /api
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://api.caro.care/api').replace(/\/api\/?$/, '');

// Socket.io connection for real-time updates
let socket = null;
let subscribedHubs = new Set();
let subscribedTests = new Set();

/**
 * Initialize WebSocket connection for EnOcean events
 * @returns {Socket} The socket.io client instance
 */
export const initializeWebSocket = () => {
  if (socket && socket.connected) {
    return socket;
  }

  const wsUrl = `${API_BASE_URL}/enocean`;
  console.log('[EnOcean WS] Connecting to:', wsUrl);

  socket = io(wsUrl, {
    transports: ['websocket', 'polling'],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000
  });

  socket.on('connect', () => {
    console.log('[EnOcean WS] Connected to:', wsUrl, 'Socket ID:', socket.id);
    // Re-subscribe to any previously subscribed hubs
    subscribedHubs.forEach(hubId => {
      socket.emit('subscribe:hub', hubId);
    });
    // Re-subscribe to any previously subscribed tests
    subscribedTests.forEach(testUUID => {
      socket.emit('subscribe:test', testUUID);
    });
  });

  socket.on('disconnect', (reason) => {
    console.log('[EnOcean WS] Disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('[EnOcean WS] Connection error:', error);
  });

  return socket;
};

/**
 * Get the current WebSocket instance
 * @returns {Socket|null}
 */
export const getSocket = () => socket;

/**
 * Subscribe to events for a specific hub
 * @param {number} hubDeviceId - The hub device ID
 * @param {Object} handlers - Event handlers
 */
export const subscribeToHub = (hubDeviceId, handlers = {}) => {
  if (!socket) {
    initializeWebSocket();
  }

  subscribedHubs.add(hubDeviceId);
  socket.emit('subscribe:hub', hubDeviceId);

  // Set up event handlers
  if (handlers.onDiscovered) {
    socket.on('button:discovered', handlers.onDiscovered);
  }
  if (handlers.onAwaitingConfirmation) {
    socket.on('button:awaiting_confirmation', handlers.onAwaitingConfirmation);
  }
  if (handlers.onConfirmed) {
    socket.on('button:confirmed', handlers.onConfirmed);
  }
  if (handlers.onLearningStarted) {
    socket.on('learning:started', handlers.onLearningStarted);
  }
  if (handlers.onLearningStopped) {
    socket.on('learning:stopped', handlers.onLearningStopped);
  }

  return () => unsubscribeFromHub(hubDeviceId, handlers);
};

/**
 * Unsubscribe from events for a specific hub
 * @param {number} hubDeviceId - The hub device ID
 * @param {Object} handlers - Event handlers to remove
 */
export const unsubscribeFromHub = (hubDeviceId, handlers = {}) => {
  if (!socket) return;

  subscribedHubs.delete(hubDeviceId);
  socket.emit('unsubscribe:hub', hubDeviceId);

  // Remove event handlers
  if (handlers.onDiscovered) {
    socket.off('button:discovered', handlers.onDiscovered);
  }
  if (handlers.onAwaitingConfirmation) {
    socket.off('button:awaiting_confirmation', handlers.onAwaitingConfirmation);
  }
  if (handlers.onConfirmed) {
    socket.off('button:confirmed', handlers.onConfirmed);
  }
  if (handlers.onLearningStarted) {
    socket.off('learning:started', handlers.onLearningStarted);
  }
  if (handlers.onLearningStopped) {
    socket.off('learning:stopped', handlers.onLearningStopped);
  }
};

/**
 * Disconnect WebSocket
 */
export const disconnectWebSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    subscribedHubs.clear();
    subscribedTests.clear();
  }
};

// ==========================================
// Button Test Subscriptions
// ==========================================

/**
 * Subscribe to events for a button test session
 * @param {string} testUUID - The test session UUID
 * @param {Object} handlers - Event handlers
 */
export const subscribeToTest = (testUUID, handlers = {}) => {
  if (!socket) {
    initializeWebSocket();
  }

  subscribedTests.add(testUUID);
  socket.emit('subscribe:test', testUUID);

  // Set up event handlers
  if (handlers.onButtonTested) {
    socket.on('button:tested', handlers.onButtonTested);
  }
  if (handlers.onButtonSkipped) {
    socket.on('button:skipped', handlers.onButtonSkipped);
  }
  if (handlers.onButtonRenamed) {
    socket.on('button:renamed', handlers.onButtonRenamed);
  }
  if (handlers.onButtonRoomLinked) {
    socket.on('button:roomLinked', handlers.onButtonRoomLinked);
  }
  if (handlers.onTestCompleted) {
    socket.on('test:completed', handlers.onTestCompleted);
  }

  return () => unsubscribeFromTest(testUUID, handlers);
};

/**
 * Unsubscribe from events for a button test session
 * @param {string} testUUID - The test session UUID
 * @param {Object} handlers - Event handlers to remove
 */
export const unsubscribeFromTest = (testUUID, handlers = {}) => {
  if (!socket) return;

  subscribedTests.delete(testUUID);
  socket.emit('unsubscribe:test', testUUID);

  // Remove event handlers
  if (handlers.onButtonTested) {
    socket.off('button:tested', handlers.onButtonTested);
  }
  if (handlers.onButtonSkipped) {
    socket.off('button:skipped', handlers.onButtonSkipped);
  }
  if (handlers.onButtonRenamed) {
    socket.off('button:renamed', handlers.onButtonRenamed);
  }
  if (handlers.onButtonRoomLinked) {
    socket.off('button:roomLinked', handlers.onButtonRoomLinked);
  }
  if (handlers.onTestCompleted) {
    socket.off('test:completed', handlers.onTestCompleted);
  }
};

// ==========================================
// Learning Mode API
// ==========================================

/**
 * Start learning mode for a hub
 * @param {number} hubDeviceId - The hub device ID
 * @returns {Promise<Object>} Learning session data
 */
export const startLearning = async (hubDeviceId) => {
  const response = await axiosInstance.post(`/enocean/learning/${hubDeviceId}/start`);
  return response.data;
};

/**
 * Stop learning mode for a hub
 * @param {number} hubDeviceId - The hub device ID
 * @returns {Promise<Object>} Stop result
 */
export const stopLearning = async (hubDeviceId) => {
  const response = await axiosInstance.post(`/enocean/learning/${hubDeviceId}/stop`);
  return response.data;
};

/**
 * Get learning status for a hub
 * @param {number} hubDeviceId - The hub device ID
 * @returns {Promise<Object>} Learning status
 */
export const getLearningStatus = async (hubDeviceId) => {
  const response = await axiosInstance.get(`/enocean/learning/${hubDeviceId}/status`);
  return response.data;
};

// ==========================================
// Button Discovery API
// ==========================================

/**
 * Get pending button discoveries for a hub
 * @param {number} hubDeviceId - The hub device ID
 * @returns {Promise<Array>} List of pending discoveries
 */
export const getPendingDiscoveries = async (hubDeviceId) => {
  const response = await axiosInstance.get(`/enocean/button/pending/${hubDeviceId}`);
  return response.data;
};

/**
 * Configure a discovered button
 * @param {number} discoveryId - The discovery ID
 * @param {Object} config - Configuration (locationId, personId, friendlyName, buttonType)
 * @returns {Promise<Object>} Configuration result
 */
export const configureButton = async (discoveryId, config) => {
  const response = await axiosInstance.post(`/enocean/button/${discoveryId}/configure`, config);
  return response.data;
};

/**
 * Cancel a pending discovery
 * @param {number} discoveryId - The discovery ID
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelDiscovery = async (discoveryId) => {
  const response = await axiosInstance.delete(`/enocean/button/${discoveryId}`);
  return response.data;
};

// ==========================================
// Button Management API
// ==========================================

/**
 * Get all registered EnOcean buttons
 * @param {Object} filters - Optional filters (locationId, personId, buttonType, status)
 * @returns {Promise<Array>} List of buttons
 */
export const getButtons = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.locationId) params.append('locationId', filters.locationId);
  if (filters.personId) params.append('personId', filters.personId);
  if (filters.buttonType) params.append('buttonType', filters.buttonType);
  if (filters.status) params.append('status', filters.status);

  const response = await axiosInstance.get(`/enocean/buttons?${params.toString()}`);
  return response.data;
};

/**
 * Update a button
 * @param {number} buttonId - The button device ID
 * @param {Object} updates - Updates (friendlyName, locationId, personId, buttonType, status)
 * @returns {Promise<Object>} Updated button
 */
export const updateButton = async (buttonId, updates) => {
  const response = await axiosInstance.put(`/enocean/buttons/${buttonId}`, updates);
  return response.data;
};

/**
 * Delete a button
 * @param {number} buttonId - The button device ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteButton = async (buttonId) => {
  const response = await axiosInstance.delete(`/enocean/buttons/${buttonId}`);
  return response.data;
};

/**
 * Get button details with last seen info
 * @param {number} buttonId - The button device ID
 * @returns {Promise<Object>} Button details
 */
export const getButtonDetails = async (buttonId) => {
  const response = await axiosInstance.get(`/enocean/buttons/${buttonId}`);
  return response.data;
};

/**
 * Unlink a button from its person (requires re-confirmation)
 * @param {number} buttonId - The button device ID
 * @returns {Promise<Object>} Unlink result
 */
export const unlinkButton = async (buttonId) => {
  const response = await axiosInstance.post(`/enocean/buttons/${buttonId}/unlink`);
  return response.data;
};

/**
 * Check if a person already has a mobile button
 * @param {number} personId - The person ID
 * @returns {Promise<Object>} Result with hasButton and button info
 */
export const checkPersonMobileButton = async (personId) => {
  const response = await axiosInstance.get(`/enocean/buttons/check-person/${personId}`);
  return response.data;
};

/**
 * Get all hubs that can be used for button discovery
 * @returns {Promise<Array>} List of hubs
 */
export const getDiscoveryHubs = async () => {
  const response = await axiosInstance.get('/enocean/hubs');
  return response.data;
};

/**
 * Get site locations and persons for a hub
 * Returns all locations in the same site as the hub's location,
 * and all persons (personType=1) in those locations.
 * @param {number} hubDeviceId - The hub device ID
 * @returns {Promise<Object>} Site data with locations and persons
 */
export const getHubSiteData = async (hubDeviceId) => {
  const response = await axiosInstance.get(`/enocean/hubs/${hubDeviceId}/site-data`);
  return response.data;
};

export default {
  // WebSocket
  initializeWebSocket,
  getSocket,
  subscribeToHub,
  unsubscribeFromHub,
  disconnectWebSocket,
  // Button Test Subscriptions
  subscribeToTest,
  unsubscribeFromTest,
  // Learning Mode
  startLearning,
  stopLearning,
  getLearningStatus,
  // Discovery
  getPendingDiscoveries,
  configureButton,
  cancelDiscovery,
  // Button Management
  getButtons,
  updateButton,
  deleteButton,
  getButtonDetails,
  unlinkButton,
  checkPersonMobileButton,
  getDiscoveryHubs,
  getHubSiteData
};
